import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import apiFetch from "../../../context/api";
import Button from "../../Elements/Button";

function today() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function formatCompact(v) {
  return new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(Number(v || 0));
}
function formatShortDate(value) {
  if (!value) return "";
  const d = new Date(value + "T00:00:00");
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "2-digit" }).format(d);
}

function MetricRow({ label, value, strong = false }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
      <span className="text-sm text-gray-600">{label}</span>
      <span className={`text-sm font-medium ${strong ? "font-semibold text-gray-900" : "text-gray-800"}`}>{value}</span>
    </div>
  );
}

export default function SociosStats({ token, venueID }) {
  const [fromDate, setFromDate] = useState(daysAgo(30));
  const [toDate, setToDate]     = useState(today());
  const [applied, setApplied]   = useState({ from: daysAgo(30), to: today() });
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async (from, to) => {
    setLoading(true);
    setError("");
    try {
      const q = new URLSearchParams({ from_date: from, to_date: to });
      if (venueID) q.set("venue_id", venueID);
      const res = await apiFetch(`/v1/reports/members?${q}`, { headers: authHeaders });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message || "Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, venueID]);

  useEffect(() => {
    if (token) fetchData(applied.from, applied.to);
  }, [token, applied, fetchData]);

  function handleFilter() {
    setApplied({ from: fromDate, to: toDate });
  }

  const chartData = useMemo(() =>
    (data?.series || []).map((item) => ({
      fecha: formatShortDate(item.date),
      Altas: Number(item.new_members || 0),
    })),
  [data]);

  return (
    <div className="flex flex-col gap-4">
      {/* Date filter bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <span className="text-sm font-medium text-gray-500 mr-1">Período</span>
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-gray-400">Desde</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-sm text-gray-800 focus:border-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--ring-selected)]"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-gray-400">Hasta</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-sm text-gray-800 focus:border-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--ring-selected)]"
          />
        </div>
        <Button onClick={handleFilter}>
          Filtrar
        </Button>
      </div>

      {loading && (
        <div className="text-center py-16 text-gray-400 text-sm">Cargando datos…</div>
      )}
      {error && (
        <div className="text-center py-10 text-red-500 text-sm">{error}</div>
      )}

      {!loading && !error && (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(280px,1fr)]">
          {/* Bar chart */}
          <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Altas de socios</h3>
              <p className="mt-0.5 text-sm text-gray-500">
                Nuevas inscripciones por día — {applied.from} a {applied.to}
              </p>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid vertical={false} strokeDasharray="4 4" stroke="#f0f0f0" />
                  <XAxis dataKey="fecha" tick={{ fontSize: 10, fill: "#9ca3af" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#9ca3af" }} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e5e7eb" }} />
                  <Bar dataKey="Altas" fill="#f59e0b" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* Stats panel */}
          <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Estado de socios</h3>
            <div className="space-y-2">
              <MetricRow label="Socios totales"  value={formatCompact(data?.totals?.total_members)} />
              <MetricRow label="Altas en período" value={formatCompact(data?.totals?.new_members)} />
              <MetricRow label="Caducan pronto"  value={formatCompact(data?.totals?.expiring_soon)} />
              <MetricRow label="Caducados"        value={formatCompact(data?.totals?.expired)} strong />
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
